import { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  SelectTabData,
  SelectTabEvent,
  Subtitle2,
  Tab,
  TabList,
} from '@fluentui/react-components';
import { BrainSparkleRegular, ChatAddRegular, Dismiss24Regular, PlayRegular } from '@fluentui/react-icons';

import { ConfirmDialog, CustomMessageBar, type TConfirmDialogState } from '@shared/components';
import { useLeaveGuard } from '@shared/hooks';
import type { TVersion } from '@modules/documents/types';
import useEditorActive from '@modules/documents/hooks/useEditorActive.hook';
import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import { hasExplicitContentSelection } from '@modules/documents/tiptap/plugins/ExplicitContentSelection';

import {
  AI_ASSISTANT_SIDEBAR_WIDTH_PX,
  AI_CONTENT_SELECTION_ELEMENT_IDS_MAX,
  AI_CONTENT_SELECTION_TEXT_MAX_CHARS,
} from '../constants';
import type { TAiAssistantStatusMessage, TAiAssistantSurface, TAiAssistantTab, TAiAssistantOrigin } from '../types';
import { useGetSelectedContent } from '../hooks/useGetSelectedContent.hook';
import useAiOpenVersion from '../hooks/useAiOpenVersion.hook';
import useGroundedQaTerminology from '../hooks/useGroundedQaTerminology.hook';
import useClearGroundedQaScrollParams from '../hooks/useClearGroundedQaScrollParams.hook';
import useAiAssistantStore, { requestAiAssistantClose } from '../stores/useAiAssistantStore';
import { GROUNDED_QA_DISCARD_MESSAGE, GROUNDED_QA_PROMPT_MAX_CHARS } from '../groundedQa/constants';

import GroundedQaAnswerView from '../groundedQa/components/GroundedQaAnswerView';
import useAiAsk from '../groundedQa/useAiAsk.hook';
import { AI_EDIT_DISCARD_MESSAGE } from '../aiEdit/constants';
import useAiEdit from '../aiEdit/useAiEdit.hook';
import useAiBudgetAvailability from '../budget/api';
import defaultAiMode from '../helpers/defaultAiMode.helper';
import toAiErrorMessage from '../helpers/toAiErrorMessage.helper';
import AiAssistantPromptView from './AiAssistantPromptView';
import AiEditReview from './AiEditReview';

type TProps = {
  surface: TAiAssistantSurface;
  origin?: TAiAssistantOrigin;
  canEditDocument?: boolean;
  onClose: () => void;
};

const AiAssistant = ({ surface, origin, canEditDocument = false, onClose }: TProps) => {
  const {
    aiMode: chosenAiMode,
    closeRequest,
    read,
    setAiMode,
    setCloseRequest,
    setPendingKind,
    update,
  } = useAiAssistantStore();
  const { prompt } = read(surface, origin);
  const [inputErrorMessage, setInputErrorMessage] = useState<string | null>(null);
  const setPrompt = (value: string) => {
    setInputErrorMessage(null);
    update(surface, origin, { prompt: value });
  };

  const aiAsk = useAiAsk({ surface, origin });
  const getSelectedContent = useGetSelectedContent();
  const { long: groundedQaLongLabel } = useGroundedQaTerminology();
  const clearGroundedQaScrollParams = useClearGroundedQaScrollParams();
  const rootRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<TAiAssistantTab>('prompt');
  const aiEdit = useAiEdit({
    surface,
    canEditDocument,
    origin,
  });
  const canUseAiAssistant = aiAsk.canUse || aiEdit.isVisible;
  const {
    budgetRefusal: availabilityRefusal,
    error: availabilityError,
    isFetching: isAvailabilityFetching,
    refetch: refetchAvailability,
  } = useAiBudgetAvailability(canUseAiAssistant);
  const isAiActive = aiAsk.isActive || aiEdit.isActive;
  const wasAiActiveRef = useRef(false);

  useEffect(() => {
    if (wasAiActiveRef.current && !isAiActive) void refetchAvailability();
    wasAiActiveRef.current = isAiActive;
  }, [isAiActive, refetchAvailability]);

  const { isNotLatestVersionOpen } = useAiOpenVersion();
  const isContentSelected = useEditorActive(hasExplicitContentSelection, false);
  const documentHtml = useContext(DocumentContext)?.content;
  const aiMode =
    chosenAiMode ?? defaultAiMode({ documentHtml, isContentSelected: isContentSelected && !isNotLatestVersionOpen });

  // Any code closing this assistant asks the store first (requestAiAssistantClose); the store
  // knows whether a run is generating from this flag.
  const pendingKind = aiEdit.isActive ? 'edit' : aiAsk.isPending ? 'ask' : null;
  useEffect(() => {
    setPendingKind(surface, pendingKind);
    return () => {
      setPendingKind(surface, null);
      const parked = useAiAssistantStore.getState().closeRequest;
      if (parked?.surface === surface) setCloseRequest(null);
    };
  }, [surface, pendingKind, setPendingKind, setCloseRequest]);

  // ONE leave guard for the whole assistant: the router only consults the LAST registered
  // blocker, so a guard per hook would silently disable the earlier one.
  useLeaveGuard(
    pendingKind !== null,
    () => (pendingKind === 'edit' ? aiEdit.cancel() : aiAsk.cancel()),
    `${pendingKind === 'edit' ? AI_EDIT_DISCARD_MESSAGE : GROUNDED_QA_DISCARD_MESSAGE} Are you sure you want to leave?`
  );

  const assistantResult = aiAsk.result || aiEdit.result;
  const hasAssistantResult = Boolean(assistantResult);
  const hasPrompt = prompt.trim().length > 0;
  const budgetRefusal = availabilityRefusal ?? aiEdit.budgetRefusal ?? aiAsk.budgetRefusal;
  const canSubmitAsk =
    !isAvailabilityFetching && !budgetRefusal && aiAsk.canUse && !aiAsk.isActive && !aiEdit.isActive && hasPrompt;
  const canSubmitEdit =
    !isAvailabilityFetching && !budgetRefusal && aiEdit.canUse && !aiAsk.isActive && !aiEdit.isActive && hasPrompt;
  const statusMessage = ((): TAiAssistantStatusMessage | null => {
    if (budgetRefusal) {
      return { text: budgetRefusal.message || 'AI is unavailable for this organization.', intent: 'error' };
    }
    if (inputErrorMessage) return { text: inputErrorMessage, intent: 'error' };
    const runStatusMessage = aiEdit.statusMessage ?? aiAsk.statusMessage;
    if (runStatusMessage) return runStatusMessage;
    if (availabilityError && !isAiActive) return { text: toAiErrorMessage(availabilityError), intent: 'error' };
    return null;
  })();

  const validateInput = async (inputLabel: 'Question' | 'Instruction') => {
    setInputErrorMessage(null);
    if (prompt.length > GROUNDED_QA_PROMPT_MAX_CHARS) {
      setInputErrorMessage(`${inputLabel} cannot exceed ${GROUNDED_QA_PROMPT_MAX_CHARS.toLocaleString()} characters.`);
      return null;
    }
    const selection = isNotLatestVersionOpen ? undefined : ((await getSelectedContent()) ?? undefined);
    if (selection && selection.text.length > AI_CONTENT_SELECTION_TEXT_MAX_CHARS) {
      setInputErrorMessage(
        `Selected content cannot exceed ${AI_CONTENT_SELECTION_TEXT_MAX_CHARS.toLocaleString()} characters. Select a smaller passage.`
      );
      return null;
    }
    if (selection && selection.elementIds.length > AI_CONTENT_SELECTION_ELEMENT_IDS_MAX) {
      setInputErrorMessage('Selected content is too large. Select a smaller passage.');
      return null;
    }
    return selection;
  };

  useEffect(() => {
    if (surface !== 'overlay') return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target || rootRef.current?.contains(target)) return;
      // The close confirmation renders in a portal outside rootRef - its own buttons must not
      // count as an outside click.
      if (target.closest('[role="dialog"]')) return;
      requestAiAssistantClose(surface, onClose);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [surface, onClose]);

  useEffect(() => {
    setTab(assistantResult ? 'answer' : 'prompt');
  }, [assistantResult]);

  const onSubmitQuestion = async () => {
    if (!canSubmitAsk) return;
    const selection = await validateInput('Question');
    if (selection === null) return;
    clearGroundedQaScrollParams();
    aiEdit.clearResult();
    aiAsk.submit({
      aiMode,
      prompt,
      origin: origin ? { ...origin, contentSelection: selection ?? undefined } : undefined,
    });
  };

  const onSubmitEdit = async () => {
    if (!canSubmitEdit) return;
    const selection = await validateInput('Instruction');
    if (selection === null) return;
    clearGroundedQaScrollParams();
    aiAsk.clearResult();
    aiEdit.submit(prompt.trim(), selection ?? undefined, aiMode);
  };

  const onContinueEdit = () => {
    setPrompt(aiEdit.followUpPrompt!);
    setTab('prompt');
  };

  const startNew = async () => {
    if (aiEdit.result?.status === 'review') await aiEdit.reject();
    aiEdit.clearResult();
    setPrompt('');
    clearGroundedQaScrollParams();
    aiAsk.clearResult();
  };

  const handleClose = () => requestAiAssistantClose(surface, onClose);

  // A close parked by requestAiAssistantClose becomes a normal local confirm dialog and the
  // request is consumed - from here on it opens and closes like every other dialog in the app.
  const [closeConfirmation, setCloseConfirmation] = useState<TConfirmDialogState>();
  const myCloseRequest = closeRequest?.surface === surface ? closeRequest : null;
  useEffect(() => {
    if (!myCloseRequest) return;
    const isEditClose = myCloseRequest.kind === 'edit';
    const confirmation: TConfirmDialogState = {
      isOpen: true,
      title: isEditClose ? 'Discard document edit?' : 'Cancel AI request?',
      content: isEditClose ? AI_EDIT_DISCARD_MESSAGE : GROUNDED_QA_DISCARD_MESSAGE,
      buttonLabel: isEditClose ? 'Discard and close' : 'Cancel and close',
      danger: true,
      onConfirm: () => {
        if (isEditClose) aiEdit.cancel();
        else aiAsk.cancel();
        setPendingKind(surface, null);
        setCloseConfirmation({ ...confirmation, isOpen: false });
        myCloseRequest.proceed();
      },
    };
    setCloseConfirmation(confirmation);
    setCloseRequest(null);
  }, [myCloseRequest]);

  // Edit with no draft asks before creating one. Cancel (button, X or Escape) drops the request
  // and leaves the typed prompt in the store; confirm hands the request back to the hook.
  const [draftConfirmation, setDraftConfirmation] = useState<TConfirmDialogState>();
  const documentVersion = useContext(DocumentContext)?.version;
  const { draftConfirmRequest, confirmCreateDraft, cancelCreateDraft } = aiEdit;
  useEffect(() => {
    if (!draftConfirmRequest) {
      setDraftConfirmation(undefined);
      return;
    }
    // Asked only without a draft, and AI edit is available only on a loaded current version: a released one.
    const versionLabel = (documentVersion as TVersion).id;
    setDraftConfirmation({
      isOpen: true,
      title: 'Create draft?',
      content: `AI edit requires a draft. Version ${versionLabel} stays unchanged.`,
      buttonLabel: 'Create draft and edit',
      onConfirm: () => {
        setDraftConfirmation((state) => state && { ...state, isOpen: false });
        confirmCreateDraft();
      },
    });
  }, [draftConfirmRequest]);

  return (
    <div
      ref={rootRef}
      className={`flex flex-col h-full ${surface === 'overlay' ? 'px-4 shrink-0' : ''}`}
      style={{
        width: surface === 'sidebar' ? `${AI_ASSISTANT_SIDEBAR_WIDTH_PX}px` : '100%',
        minWidth: surface === 'sidebar' ? `${AI_ASSISTANT_SIDEBAR_WIDTH_PX}px` : undefined,
      }}
    >
      <ConfirmDialog state={closeConfirmation} setState={setCloseConfirmation} />
      <ConfirmDialog
        state={draftConfirmation}
        setState={(state) => {
          setDraftConfirmation(state);
          if (!state.isOpen) cancelCreateDraft();
        }}
      />

      <Dialog
        open={aiEdit.isSelectionReminderOpen}
        onOpenChange={(_, data) => aiEdit.setSelectionReminderOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              No content selected
            </DialogTitle>
            <DialogContent className="space-y-3">
              <CustomMessageBar intent="warning" body="Reading the whole document uses far more credits." />
              <div>Select the part you want changed, or name it in your prompt.</div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" className="!whitespace-nowrap" onClick={aiEdit.selectTextFirst}>
                Select content
              </Button>
              <Button appearance="secondary" className="!whitespace-nowrap" onClick={aiEdit.continueWithoutSelection}>
                Continue without selection
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <div className="flex justify-between items-center px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <span className="!text-aiAccentSecondary">
            <BrainSparkleRegular fontSize="24px" />
          </span>
          <Subtitle2>{aiAsk.canUse ? groundedQaLongLabel : 'Edit document'}</Subtitle2>
        </div>
        <Button appearance="subtle" size="small" aria-label="Close" icon={<Dismiss24Regular />} onClick={handleClose} />
      </div>

      {!aiEdit.result && aiEdit.followUpPrompt && (
        <div className="px-4 pb-2">
          <Button appearance="secondary" icon={<PlayRegular />} onClick={onContinueEdit}>
            Continue
          </Button>
        </div>
      )}

      {hasAssistantResult && (
        <div className="flex items-center gap-3 px-4 pb-2">
          {aiEdit.result?.status !== 'review' && (
            <Button appearance="secondary" className="!min-w-0" icon={<ChatAddRegular />} onClick={startNew}>
              New
            </Button>
          )}
          <TabList
            selectedValue={tab}
            size="small"
            onTabSelect={(_: SelectTabEvent, data: SelectTabData) => setTab(data.value as TAiAssistantTab)}
          >
            <Tab id="qa-tab-prompt" value="prompt">
              Prompt
            </Tab>
            <Tab id="qa-tab-answer" value="answer">
              Answer
            </Tab>
          </TabList>
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col" role="tabpanel" aria-labelledby={`qa-tab-${tab}`}>
        {tab === 'answer' && aiEdit.result ? (
          <AiEditReview
            key={aiEdit.result.runId}
            result={aiEdit.result}
            surface={surface}
            origin={origin}
            isBusy={aiEdit.isBusy}
            changeIndex={aiEdit.changeIndex}
            onPrevious={() => aiEdit.navigate(aiEdit.changeIndex - 1)}
            onNext={() => aiEdit.navigate(aiEdit.changeIndex + 1)}
            onAccept={() => void aiEdit.accept()}
            onReject={aiEdit.reject}
            onCancel={aiEdit.cancel}
            onRevert={aiEdit.canRevert ? () => void aiEdit.revert() : undefined}
            submittedFeedback={aiEdit.submittedFeedback}
            onFeedbackSubmitted={aiEdit.setSubmittedFeedback}
            onContinue={aiEdit.followUpPrompt ? onContinueEdit : undefined}
          />
        ) : tab === 'answer' && aiAsk.result ? (
          <GroundedQaAnswerView key={aiAsk.result.runId} response={aiAsk.result} surface={surface} origin={origin} />
        ) : (
          <AiAssistantPromptView
            value={prompt}
            onChange={setPrompt}
            onSubmitAsk={aiAsk.canUse ? onSubmitQuestion : undefined}
            onSubmitEdit={aiEdit.isVisible ? onSubmitEdit : undefined}
            isEditAvailable={aiEdit.isAvailable}
            isAskPending={aiAsk.isPending}
            isEditPending={aiEdit.isPending}
            aiMode={aiMode}
            isAiModeChangeDisabled={aiAsk.isActive || aiEdit.isActive}
            onAiModeChange={setAiMode}
            isContentSelected={isContentSelected || aiEdit.hasSelectionContext}
            canSubmitAsk={canSubmitAsk}
            canSubmitEdit={canSubmitEdit}
            editDisabledReason={aiEdit.disabledReason}
            statusMessage={statusMessage}
            pendingRun={aiAsk.pendingRun}
            editProgressMessage={aiEdit.progressMessage}
            onCancel={aiEdit.isPending ? aiEdit.cancel : aiAsk.isPending ? aiAsk.cancel : undefined}
            canCancel={aiEdit.isPending || aiAsk.canCancel}
          />
        )}
      </div>
    </div>
  );
};

export default AiAssistant;
